/**
 * QMP-FE-2 (F1) — THE GRAMMAR OF THE RULE ROWS, steps 2a / 2b / 2c. PURE:
 * no React, no api. Fed with `GET /qmp` (`structure`, `packages`,
 * `standard_rules`), it answers what the screen shows and what the save sends.
 *
 * THE GRAMMAR (mockup v79 `rt()`, D11.1, D14.1). A subarea opens with a first
 * row — "All packages", or "All other packages" once some package has a row of
 * its own — and below it the packages that need something else:
 *   · "Own rule"   — born as a copy of the first row, removed back to it;
 *   · "automatic · <reason>" (2a only) — a package that lacks a party the
 *     first row names follows an ADAPTED copy of it (mockup `why2a` /
 *     `adapt`; the server proposes with the same `qmp_adapt_rule`). It has no
 *     remove: it exists because of the package's own parties.
 * THE FIRST ROW IS NEVER SAVED. It is an editing tool: the save sends EVERY
 * live package with its EXPLICIT value (D11.1, D12.9, Q-17).
 *
 * THE FIRST ROW ON REOPENING is the rule most packages saved, ties broken in
 * favour of the standard rule, then by the smallest canonical form — the
 * server's own `qmp_general_rules`, so it is the rule the server adapted its
 * `proposed` values from.
 *
 * Values are compared by CANONICAL FORM (sorted keys, no spaces — the
 * server's `_qmp_canonical`), never by reference or key order.
 *
 * QMP-FE-4 — THE SAME GRAMMAR FOR STEPS 4 AND 5 (`ncr`, `rfi`, `visibility`,
 * `ai`). What changes is what "follow the first row" means for a package:
 *   · NCR / RFI deadlines that "Vary between companies" are kept per company,
 *     and a package takes the companies it HAS (`fitRule`, the server's
 *     `_qmp_refit_dues`) — the first row names all three parties;
 *   · a subarea that does not exist in a package — RFIs in internal QA
 *     (D14.7), visibility without three parties (D14.8) — has ONE valid value,
 *     `null`: that package is always "automatic" and the save sends `null` for
 *     it, explicitly;
 *   · an NCR rule set per company cannot apply to a package with one company:
 *     it is automatic, collapsed as the server collapses it (`qmp_adapt_rule`).
 *
 * QMP-FE-3 — THE SAME GRAMMAR FOR STEP 3 (`handover`, `punch_list`). Two
 * things are new:
 *   · a package can only walk a path it HAS THE PARTIES FOR **and that ends at
 *     its last party** (server `qmp_rule_structure_errors`, section
 *     `handover`). The live mockup renderer checks only the first half
 *     (`pathFits`, v79:1126); the mockup's own `miss3a` (v79:766) checks both,
 *     and that is the one the contract speaks. A package the first row's path
 *     does not fit is automatic, on the path its chain walks;
 *   · `punch_list` is the ONE subarea that depends on another: a rule set per
 *     handover needs two handovers, which is the package's HANDOVER PATH
 *     (server `_qmp_saved_handover_path`, else the chain). `ctx.handoverPathOf`
 *     carries it; everything else stays per-subarea.
 * `route_returns_to_a_company` is NOT judged here — it is about companies, so
 * the value is sent and the server's refusal is drawn on the package's row.
 */

export const STEP2_SECTIONS = ['itp_rules', 'hold_points', 'witness_points'];
export const STEP3_SECTIONS = ['handover', 'punch_list'];
export const STEP4_SECTIONS = ['ncr', 'rfi', 'visibility'];
export const STEP5_SECTIONS = ['ai'];
// `handover` before `punch_list`: the second reads the first's path.
export const RULE_SECTIONS = STEP2_SECTIONS.concat(
  STEP3_SECTIONS, STEP4_SECTIONS, STEP5_SECTIONS,
);

// ---------------------------------------------------------------------------
// canonical form
// ---------------------------------------------------------------------------

export function canonical(v) {
  if (v === undefined || v === null) return 'null';
  if (typeof v !== 'object') return JSON.stringify(v);
  if (Array.isArray(v)) return `[${v.map(canonical).join(',')}]`;
  return `{${Object.keys(v).filter((k) => v[k] !== undefined).sort()
    .map((k) => `${JSON.stringify(k)}:${canonical(v[k])}`).join(',')}}`;
}

export const sameValue = (a, b) => canonical(a) === canonical(b);

const clone = (v) => (v === undefined || v === null ? null : JSON.parse(JSON.stringify(v)));

// ---------------------------------------------------------------------------
// a package as the rules see it
// ---------------------------------------------------------------------------

/** The positions that EXIST in a `structure[]` row, in chain order. */
export const positionsOf = (row) => ['execution']
  .concat(row && row.verification ? ['verification'] : [])
  .concat(row && row.acceptance ? ['acceptance'] : []);

const isInternalQa = (row) => !(row && row.verification) && !(row && row.acceptance);
const isFullChain = (row) => Boolean(row && row.verification && row.acceptance);

/** The subarea does not exist in this package, so `null` is its only value:
 *  RFIs in internal QA (D14.7), visibility without three parties (D14.8).
 *  `row` null = the first row, where every subarea exists. */
export function forcedNull(section, row) {
  if (!row) return false;
  if (section === 'rfi') return isInternalQa(row);
  if (section === 'visibility') return !isFullChain(row);
  return false;
}

// Sections whose saved or proposed value may be `null` (NL21-A2 DV-7).
const NULLABLE = new Set(['rfi', 'visibility']);
const has = (o, k) => Boolean(o) && Object.prototype.hasOwnProperty.call(o, k);

// ---------------------------------------------------------------------------
// §19 — "If the receiving company does not sign" (QMP-BE-2, D22)
// ---------------------------------------------------------------------------

/** `receiver_signature_limit`: `{"kind": "none"}` ("No time limit", the
 *  standard) or `{"kind": "days", "value": N >= 1}` ("Time limit"). */
export const NO_TIME_LIMIT = { kind: 'none' };

/** A handover rule saved before the limit existed has no key: it reads "No
 *  time limit" — the server's own default. */
export const receiverLimitOf = (v) => (v && v.receiver_signature_limit) || NO_TIME_LIMIT;

/** A handover rule with its limit spelt out, so an old rule and the same rule
 *  saved today compare equal (`sameValue`) and never show as unsaved. */
export function withReceiverLimit(v) {
  if (!v || typeof v !== 'object' || has(v, 'receiver_signature_limit')) return v;
  return { ...v, receiver_signature_limit: { ...NO_TIME_LIMIT } };
}

/** The words of the field, the summaries and the rule sheet. */
export const RECEIVER_LIMIT_LABEL = 'If the receiving company does not sign';

/** How a limit reads on the summaries and the rule sheet. */
export const formatReceiverLimit = (limit) => {
  const l = limit || NO_TIME_LIMIT;
  if (l.kind !== 'days') return 'No time limit';
  return l.value == null ? 'Time limit' : formatDuration({ value: l.value, unit: 'working_days' });
};

/** Every section read from the plan goes through here: the handover rule gets
 *  its limit spelt out; every other section is as served. */
const onRead = (section, v) => (section === 'handover' ? withReceiverLimit(v) : v);

/** What the package holds for a section: draft ?? proposed ?? current ?? standard.
 *  In `rfi` / `visibility` a `null` is a value, not an absence. */
export function packageValue(section, entry, standardRules, row) {
  return onRead(section, readPackageValue(section, entry, standardRules, row));
}

function readPackageValue(section, entry, standardRules, row) {
  const nullable = NULLABLE.has(section);
  const draft = entry && entry.draft;
  if (draft && (draft.sections_saved || []).includes(section)) {
    if (draft[section] != null) return draft[section];
    if (nullable && has(draft, section)) return null;
  }
  const proposed = entry && entry.proposed;
  if (proposed && proposed[section] != null) return proposed[section];
  if (nullable && has(proposed, section)) return null;
  const current = entry && entry.current && entry.current.rules;
  if (current && current[section] != null) return current[section];
  if (nullable && has(current, section)) return null;
  if (forcedNull(section, row)) return null;
  return (standardRules && standardRules[section]) ?? null;
}

// ---------------------------------------------------------------------------
// deadlines per company — `ncr` and `rfi` (D14.6, D14.7)
// ---------------------------------------------------------------------------

const DUE_KEY = { execution: 'executing', verification: 'verification', acceptance: 'acceptance' };
/** The position a per-company key stands for. */
export const DUE_POSITION = { executing: 'execution', verification: 'verification', acceptance: 'acceptance' };
export const DUE_FIELDS = { ncr: ['response_due', 'decision_due'], rfi: ['answer_due'] };

/** The per-company keys a deadline takes: the parties the package has (all
 *  three on the first row); the Acceptance Party never fixes, so never in an
 *  NCR's response due (`acceptance_never_fixes`). */
export function dueKeys(section, field, row) {
  const keys = (row ? positionsOf(row) : ['execution', 'verification', 'acceptance'])
    .map((p) => DUE_KEY[p]);
  return section === 'ncr' && field === 'response_due'
    ? keys.filter((k) => k !== 'acceptance') : keys;
}

/** "Varies between companies" needs two companies (`canSplit`,
 *  `per_company_needs_two_companies`). */
export const canVaryByCompany = (row) => !row || !isInternalQa(row);

// ---------------------------------------------------------------------------
// the handover path — step 3a (D14.4)
// ---------------------------------------------------------------------------

/** Server `QMP_HANDOVER_PATH_POSITIONS` / mockup `PATHPOS`: the positions each
 *  path of the contract walks (the mockup spells the last one `NONE`; the wire
 *  says `none`). The paths offered, and their labels, are the server's
 *  (`options.handover.path`). */
export const PATH_POSITIONS = {
  EVA: ['execution', 'verification', 'acceptance'],
  EA: ['execution', 'acceptance'],
  EV: ['execution', 'verification'],
  none: ['execution'],
};

/** Server `QMP_HANDOVER_PATH_COUNTS`: how many handovers a path has. */
export const PATH_HANDOVER_COUNT = {
  EVA: 2, EA: 1, EV: 1, none: 0,
};

/** `"E"` | `"EV"` | `"EA"` | `"EVA"` — the server's `structure.chain`. */
export const chainOf = (row) => `E${row && row.verification ? 'V' : ''}${row && row.acceptance ? 'A' : ''}`;

/** The path a chain walks by itself (server `qmp_adapt_rule`, section
 *  `handover`; mockup `fitPath`). */
export const chainPath = (row) => ({
  EVA: 'EVA', EV: 'EV', EA: 'EA', E: 'none',
}[chainOf(row)]);

/** Can this package walk this path? The server's two structural conditions:
 *  every position of the path exists, AND the path ends at the package's last
 *  party (`path_needs_a_party_the_package_lacks`,
 *  `path_must_end_at_the_last_party`). `row` null = the first row, which walks
 *  any path. */
export function pathFitsRow(path, row) {
  if (!row) return true;
  const need = PATH_POSITIONS[path] || [];
  const have = positionsOf(row);
  if (need.some((p) => !have.includes(p))) return false;
  return need[need.length - 1] === have[have.length - 1];
}

/** The hops a path makes: `[{index, from, to}]` by position (mockup `hopsQ`
 *  / `stages`). One entry per item of `handovers[]`. */
export function handoverHops(path) {
  const seq = PATH_POSITIONS[path] || [];
  return seq.slice(1).map((to, i) => ({ index: i, from: seq[i], to }));
}

/** Mockup `UNIT_LBL` (v79:906) — how a saved unit reads on the summaries. */
export const HANDOVER_UNIT_LABEL = { asset: 'Asset by asset', package: 'Whole package' };
export const formatHandoverUnit = (u) => HANDOVER_UNIT_LABEL[u] || '';

/** A handover notice is never "No minimum notice": `QmpHandoverStep.notice`
 *  is a `Duration`, not `Optional`. */
export const formatHandoverNotice = (d) => `${formatDuration(d)} before`;
/** Mockup `shortNotice`: "2 working days before" -> "2 working days' notice". */
export const formatShortNotice = (d) => `${formatDuration(d)}' notice`;
/** Mockup `nh` (v79:1199). */
export const formatHandoverCount = (path) => ({
  2: '2 handovers on this path', 1: '1 handover on this path',
}[PATH_HANDOVER_COUNT[path]] || '');

/** The served standard's `handovers[]` (`standard_rules.handover`), the items a
 *  refit invents when the value does not have them. */
export const standardHandovers = (standardRules) => (
  (standardRules && standardRules.handover && standardRules.handover.handovers) || []);

/** `handovers[]` refitted to a path, exactly as the server refits it
 *  (`qmp_adapt_rule`, section `handover`): going down to one handover, `EA`
 *  keeps the LAST of the pool — the mockup's `q.hu1 = S.gen.hu2` — and `EV`
 *  the first. `std` = the served standard's `handovers[]`. */
export function refitHandovers(pool, path, std) {
  const list = pool || [];
  const s = std || [];
  if (path === 'EVA') {
    return [clone(list[0]) || clone(s[0]),
      clone(list[1]) || clone(s[1])];
  }
  if (path === 'EA') return [clone(list[list.length - 1]) || clone(s[1])];
  if (path === 'EV') return [clone(list[0]) || clone(s[0])];
  return [];
}

/** Choosing "No time limit" / "Time limit" and its number of working days. */
export function setReceiverLimit(value, limit) {
  return { ...(clone(value) || {}), receiver_signature_limit: clone(limit) };
}

/** Choosing a path on the screen: the items follow the path it now has. */
export function setHandoverPath(value, path, std) {
  const v = clone(value) || {};
  if (v.path === path) return { ...v, path };
  return { ...v, path, handovers: refitHandovers(v.handovers, path, std) };
}

// ---------------------------------------------------------------------------
// the punch list — step 3b (D14.5)
// ---------------------------------------------------------------------------

/** D14.5 — A always blocks, so only B and C may stay open: the categories
 *  offered are the server's (`options.punch_list["handovers[].open_categories"]`). */
export const EMPTY_PUNCH_STEP = { qa_complete_with_open_items: false, open_categories: [] };

/** "Varies between companies" (D14.5's own words) needs two handovers — the
 *  package's saved handover path, else its chain (server `punch_list` branch
 *  of `qmp_rule_structure_errors`). The first row always has them. */
export function punchHasTwoHandovers(row, handoverPath) {
  if (!row) return true;
  return handoverPath != null ? handoverPath === 'EVA' : chainOf(row) === 'EVA';
}

/** The "Varies" / "Same" link. On: the second handover starts as a copy of the
 *  first (mockup `pl_open2 = pl_open`). Off: the first is what remains. */
export function setPerHandover(value, on) {
  const list = ((value && value.handovers) || []).map(clone);
  const first = list[0] || clone(EMPTY_PUNCH_STEP);
  return on
    ? { per_handover: true, handovers: [first, list[1] || clone(first)] }
    : { per_handover: false, handovers: [first] };
}

/** One handover's answer. Choosing "No" clears the categories (mockup:
 *  `if(v==="No") q.pl_cats=[]`). */
export function setPunchStep(value, index, patch) {
  const v = clone(value) || clone({ per_handover: false, handovers: [] });
  const list = ((v.handovers) || []).map(clone);
  const next = { ...(list[index] || clone(EMPTY_PUNCH_STEP)), ...patch };
  if (!next.qa_complete_with_open_items) next.open_categories = [];
  list[index] = next;
  return { ...v, handovers: list };
}

/** Mockup `plInvalid` + its Continue guard (v79:1571, 1580): the screen does
 *  not send a body it can see is incomplete. The server refuses the same thing
 *  (`at_least_one_when_open_items_allowed`) and its refusal is still drawn. */
export const PUNCH_NEEDS_A_CATEGORY = 'Every rule set to Yes must allow at least one category.';

export function sectionBlocker(section, state, structure) {
  if (section !== 'punch_list' || !state) return null;
  const values = [state.general].concat((structure || []).map((r) => valueOf(state, r.qwp_id)));
  const bad = values.some((v) => ((v && v.handovers) || []).some(
    (h) => h.qa_complete_with_open_items && !(h.open_categories || []).length,
  ));
  return bad ? PUNCH_NEEDS_A_CATEGORY : null;
}

/** The handover path a package holds in the editor right now — what
 *  `punch_list` needs, and what the server will read when 3b is saved (the
 *  wizard saves 3a first). */
export function handoverPathOf(state, qwpId) {
  const v = state ? valueOf(state, qwpId) : null;
  return (v && v.path) || null;
}

/** The context `punch_list` is read and reconciled with. */
export const withHandover = (ctx, handoverState) => ({
  ...ctx, handoverPathOf: (id) => handoverPathOf(handoverState, id),
});

const handoverPathIn = (ctx, row) => (ctx && ctx.handoverPathOf && row
  ? ctx.handoverPathOf(row.qwp_id) : null);

// The server's `next(iter(dues.values()))`: its first rows come from the
// canonical form, whose keys are sorted — so the first key, alphabetically.
const firstDue = (dues) => {
  const k = Object.keys(dues || {}).sort()[0];
  return k === undefined ? null : clone(dues[k]);
};

/** `_qmp_refit_dues`: the keys given; a missing one takes the first value. */
function refitDues(dues, keys) {
  const d = dues || {};
  const first = firstDue(d);
  const out = {};
  keys.forEach((k) => { out[k] = has(d, k) ? clone(d[k]) : clone(first); });
  return out;
}

/** The first row as a package that FOLLOWS it holds it: deadlines set per
 *  company list the companies the package has. `row` null = the first row
 *  itself, with all three parties. Every other section: a copy. */
export function fitRule(section, general, row) {
  const v = clone(general);
  if (!v || !DUE_FIELDS[section] || !v.per_company) return v;
  DUE_FIELDS[section].forEach((f) => { v[f] = refitDues(v[f], dueKeys(section, f, row)); });
  return v;
}

/** "Varies between companies" / "Same for all companies" on a value. On: each
 *  company of the row starts from the single time. Off: the single time is
 *  the first company's (the Executing Party's) — the value has nowhere to keep
 *  the time it had before it was split. */
export function setPerCompany(section, value, row, on) {
  const v = clone(value) || {};
  (DUE_FIELDS[section] || []).forEach((f) => {
    const d = v[f] || {};
    const keys = dueKeys(section, f, row);
    if (on) {
      const one = has(d, 'all') ? clone(d.all) : firstDue(d);
      v[f] = {};
      keys.forEach((k) => { v[f][k] = clone(one); });
    } else {
      const k = keys.find((x) => has(d, x));
      v[f] = { all: k ? clone(d[k]) : firstDue(d) };
    }
  });
  v.per_company = Boolean(on);
  return v;
}

/** Q-14, as the server computes it (`qmp_general_rules`). */
export function generalRule(section, structure, packages, standardRules) {
  const standard = onRead(section, clone(standardRules && standardRules[section]));
  const counts = new Map();
  (structure || []).forEach((row) => {
    const draft = packages && packages[row.qwp_id] && packages[row.qwp_id].draft;
    if (draft && (draft.sections_saved || []).includes(section) && draft[section] != null) {
      const key = canonical(onRead(section, draft[section]));
      counts.set(key, (counts.get(key) || 0) + 1);
    }
  });
  if (!counts.size) return standard;
  const top = Math.max(...counts.values());
  const tied = [...counts.keys()].filter((k) => counts.get(k) === top).sort();
  const std = canonical(standard);
  return JSON.parse(tied.includes(std) ? std : tied[0]);
}

// ---------------------------------------------------------------------------
// "automatic · <reason>" — 2a (mockup `why2a`, `adapt`), and in steps 4 the
// packages a subarea does not fit (QMP-FE-4, with the same three reasons)
// ---------------------------------------------------------------------------

export const AUTO_INTERNAL_QA = 'internal QA — no Verification or Acceptance Party';
export const AUTO_NO_ACCEPTANCE = 'no Acceptance Party';
export const AUTO_NO_VERIFICATION = 'no Verification Party';
/** Mockup `why3a` (v79:1136) — internal QA reads differently in step 3. */
export const AUTO_HANDOVER_INTERNAL_QA = 'internal QA — no handover';
/** Mockup `miss3a` (v79:767), the two reasons the live renderer never reaches. */
export const AUTO_PATH_HAS_A_PARTY = 'this package has a party to hand over to';
export const autoPathMustEndAt = (row) => `must end at the ${POSITION_LABEL[positionsOf(row).slice(-1)[0]]}`;
/** 3b — copy nova: the mockup never draws an automatic punch-list row. */
export const AUTO_ONE_HANDOVER = 'only one handover on this path';

const lackingReason = (row) => {
  if (isInternalQa(row)) return AUTO_INTERNAL_QA;
  return row && row.verification ? AUTO_NO_ACCEPTANCE : AUTO_NO_VERIFICATION;
};

/** Why a package cannot follow the first row's handover path, or null. */
function whyPath(path, row) {
  if (pathFitsRow(path, row)) return null;
  const need = PATH_POSITIONS[path] || [];
  if (need.some((p) => !positionsOf(row).includes(p))) {
    if (isInternalQa(row)) return AUTO_HANDOVER_INTERNAL_QA;
    return row.verification ? AUTO_NO_ACCEPTANCE : AUTO_NO_VERIFICATION;
  }
  return path === 'none' ? AUTO_PATH_HAS_A_PARTY : autoPathMustEndAt(row);
}

/** Why a package cannot follow the first row as it is, or null.
 *  `handoverPath` is only read by `punch_list`. */
export function whyAutomatic(section, general, row, handoverPath) {
  if (forcedNull(section, row)) return lackingReason(row);
  if (section === 'handover') {
    return row ? whyPath((general && general.path) || 'none', row) : null;
  }
  if (section === 'punch_list') {
    return general && general.per_handover && !punchHasTwoHandovers(row, handoverPath)
      ? AUTO_ONE_HANDOVER : null;
  }
  if (section === 'ncr') {
    return general && general.per_company && !canVaryByCompany(row) ? AUTO_INTERNAL_QA : null;
  }
  if (section !== 'itp_rules' || !general) return null;
  const endorsement = general.endorsement || {};
  const named = (general.prepared_by || [])
    .concat(endorsement.required ? (endorsement.endorsed_by || []) : [])
    .map((ref) => ref && ref.position)
    .filter((p) => p === 'verification' || p === 'acceptance');
  const have = positionsOf(row);
  const lack = named.filter((p, i, a) => a.indexOf(p) === i && !have.includes(p));
  if (!lack.length) return null;
  if (isInternalQa(row)) return AUTO_INTERNAL_QA;
  return lack.includes('acceptance') ? AUTO_NO_ACCEPTANCE : AUTO_NO_VERIFICATION;
}

/** The first row FITTED to a package (server `qmp_adapt_rule`, mockup `adapt`).
 *  `handoverPath` is only read by `punch_list`; `standardRules` (the served
 *  standard) only by `handover`, for the items a refit must invent. */
export function adaptRule(section, general, row, liveCompanyIds, handoverPath, standardRules) {
  if (forcedNull(section, row)) return null;
  const v = clone(general);
  if (section === 'handover') {
    return v ? setHandoverPath(v, chainPath(row), standardHandovers(standardRules)) : v;
  }
  if (section === 'punch_list') {
    if (!v || !v.per_handover || punchHasTwoHandovers(row, handoverPath)) return v;
    return { ...v, per_handover: false, handovers: (v.handovers || []).slice(0, 1) };
  }
  if (section === 'ncr' && v && v.per_company && !canVaryByCompany(row)) {
    return {
      per_company: false,
      response_due: { all: firstDue(v.response_due) },
      decision_due: { all: firstDue(v.decision_due) },
    };
  }
  if (section !== 'itp_rules' || !v) return fitRule(section, v, row);
  const have = positionsOf(row);
  const keep = (ref) => (ref && ref.position
    ? have.includes(ref.position)
    : !liveCompanyIds || liveCompanyIds.has(ref && ref.company_id));
  const prepared = (v.prepared_by || []).filter(keep);
  v.prepared_by = prepared.length ? prepared : [{ position: 'execution' }];
  const e = v.endorsement || {};
  e.endorsed_by = (e.endorsed_by || []).filter(keep);
  if (e.required && !e.endorsed_by.length) {
    e.endorsed_by = [{
      position: have.includes('verification') ? 'verification'
        : have.includes('acceptance') ? 'acceptance' : 'execution',
    }];
  }
  v.endorsement = e;
  return v;
}

// ---------------------------------------------------------------------------
// the editor state of one subarea: { general, rows: {qwp_id: {kind, value, why}} }
// kind: 'follow' (the first row) | 'own' | 'auto'
// ---------------------------------------------------------------------------

/** Mockup `autoExc`: re-derive the automatic rows against a first row.
 *  `force` = the first row changed, so automatic rows are re-adapted. */
function reconcile(section, general, rows, ctx, force) {
  const out = {};
  (ctx.structure || []).forEach((row) => {
    const id = row.qwp_id;
    const cur = rows[id] || { kind: 'follow' };
    const hp = handoverPathIn(ctx, row);
    const why = whyAutomatic(section, general, row, hp);
    // One valid value (null): never an own rule, whatever was there.
    if (forcedNull(section, row)) { out[id] = { kind: 'auto', why, value: null }; return; }
    if (cur.kind === 'own') { out[id] = cur; return; }
    if (why) {
      out[id] = (cur.kind === 'auto' && !force)
        ? { ...cur, why }
        : {
          kind: 'auto', why, value: adaptRule(section, general, row, ctx.liveCompanyIds, hp, ctx.standardRules),
        };
    } else {
      out[id] = { kind: 'follow', value: fitRule(section, general, row) };
    }
  });
  return out;
}

/** The subarea as the server holds it, read through the grammar. */
export function initSection(section, ctx) {
  const general = fitRule(section,
    generalRule(section, ctx.structure, ctx.packages, ctx.standardRules), null);
  const rows = {};
  (ctx.structure || []).forEach((row) => {
    const id = row.qwp_id;
    const value = packageValue(section, ctx.packages && ctx.packages[id], ctx.standardRules, row);
    const hp = handoverPathIn(ctx, row);
    const why = whyAutomatic(section, general, row, hp);
    if (forcedNull(section, row)) { rows[id] = { kind: 'auto', why, value: null }; return; }
    if (!why) {
      const fitted = fitRule(section, general, row);
      rows[id] = sameValue(value, fitted)
        ? { kind: 'follow', value: fitted }
        : { kind: 'own', value: clone(value) };
      return;
    }
    const adapted = adaptRule(section, general, row, ctx.liveCompanyIds, hp, ctx.standardRules);
    // A package still holding the first row it can no longer follow becomes
    // automatic with the adapted value (mockup `autoExc`) — a local change.
    rows[id] = (sameValue(value, general) || sameValue(value, adapted))
      ? { kind: 'auto', why, value: adapted }
      : { kind: 'own', value: clone(value) };
  });
  return { general, rows };
}

/** A new structure (after a reload) under local, unsaved edits: keep the
 *  person's first row and own rows, re-derive the rest. */
export function carrySection(section, local, ctx) {
  const rows = {};
  (ctx.structure || []).forEach((row) => {
    const prev = local.rows[row.qwp_id];
    if (prev) rows[row.qwp_id] = prev;
  });
  return { general: local.general, rows: reconcile(section, local.general, rows, ctx, false) };
}

/** Editing the first row: it and every package that follows it (`applyGen`). */
export function setGeneral(section, state, general, ctx) {
  const force = !sameValue(general, state.general);
  return { general: clone(general), rows: reconcile(section, general, state.rows, ctx, force) };
}

/** "+ Add a package with its own rule": a copy of the first row (`copyGen`),
 *  as that package holds it. */
export function addOwnRule(state, qwpId) {
  return { ...state, rows: { ...state.rows, [qwpId]: { kind: 'own', value: clone(valueOf(state, qwpId)) } } };
}

/** ✕ on an own row: back to the first row (then automatic if it must be). */
export function removeOwnRule(section, state, qwpId, ctx) {
  const rows = { ...state.rows, [qwpId]: { kind: 'follow' } };
  return { ...state, rows: reconcile(section, state.general, rows, ctx, false) };
}

/** Editing an own or automatic row. */
export function setPackageRule(state, qwpId, value) {
  const cur = state.rows[qwpId] || { kind: 'own' };
  return { ...state, rows: { ...state.rows, [qwpId]: { ...cur, value: clone(value) } } };
}

/** The value a package would be saved with. A package that follows the first
 *  row holds it as `fitRule` fitted it. */
export function valueOf(state, qwpId) {
  const r = state.rows[qwpId];
  if (!r) return state.general;
  if (r.kind === 'follow') return has(r, 'value') ? r.value : state.general;
  return r.value;
}

/** The PUT body: EVERY live package, each with its explicit value. */
export function buildBody(state, structure) {
  const out = {};
  (structure || []).forEach((row) => { out[row.qwp_id] = clone(valueOf(state, row.qwp_id)); });
  return out;
}

/** The body the server already holds (the baseline an edit is judged against). */
export function serverBody(section, ctx) {
  const out = {};
  (ctx.structure || []).forEach((row) => {
    out[row.qwp_id] = clone(packageValue(section, ctx.packages && ctx.packages[row.qwp_id],
      ctx.standardRules, row));
  });
  return out;
}

export const isDirty = (section, state, ctx) => !sameValue(
  buildBody(state, ctx.structure), serverBody(section, ctx),
);

// ---------------------------------------------------------------------------
// the rows as drawn
// ---------------------------------------------------------------------------

export const FIRST_ROW_ALL = 'All packages';
export const FIRST_ROW_OTHERS = 'All other packages';
export const OWN_RULE = 'Own rule';

/** First row label + sub-line, the package rows, the packages still free. */
export function lines(state, structure) {
  const all = structure || [];
  const own = all.filter((r) => (state.rows[r.qwp_id] || {}).kind !== undefined
    && state.rows[r.qwp_id].kind !== 'follow');
  const follow = all.filter((r) => !own.includes(r));
  return {
    first: {
      label: own.length ? FIRST_ROW_OTHERS : FIRST_ROW_ALL,
      sub: follow.length === 0
        ? 'no package yet — a new QWP starts on the standard rules'
        : `${follow.length} of ${all.length} packages`,
    },
    rows: own.map((r) => ({ row: r, ...state.rows[r.qwp_id] })),
    free: follow,
  };
}

// ---------------------------------------------------------------------------
// saved / needs review
// ---------------------------------------------------------------------------

/** The subarea is saved when EVERY live package has it in `sections_saved`. */
export function sectionSaved(section, structure, packages) {
  const all = structure || [];
  return all.length > 0 && all.every((row) => {
    const draft = packages && packages[row.qwp_id] && packages[row.qwp_id].draft;
    return Boolean(draft && (draft.sections_saved || []).includes(section));
  });
}

/** `{qwp_id: [{section, field, reason}]}` for this section only. */
export function needsReviewOf(section, structure, packages) {
  const out = {};
  (structure || []).forEach((row) => {
    const items = ((packages && packages[row.qwp_id] && packages[row.qwp_id].needs_review) || [])
      .filter((n) => n && n.section === section);
    if (items.length) out[row.qwp_id] = items;
  });
  return out;
}

// ---------------------------------------------------------------------------
// how a saved value reads (v79 labels), where no select draws it
// ---------------------------------------------------------------------------

const unitWord = (n, unit) => (unit === 'hours'
  ? (n === 1 ? 'hour' : 'hours')
  : (n === 1 ? 'working day' : 'working days'));

export const formatDuration = (d) => (d && d.value != null ? `${d.value} ${unitWord(d.value, d.unit)}` : '');
export const formatNotice = (d) => (d == null ? 'No minimum notice' : `${formatDuration(d)} before`);
export const formatSignoffDue = (d) => (d && d.at === 'end_of_working_day'
  ? 'End of working day' : `${formatDuration(d)} after`);
export const formatResponseTime = (d) => (d == null ? 'No deadline' : formatDuration(d));
export const formatDocRelease = (v) => (!v || v.document_release_due == null
  ? 'Set in each ITP' : formatDuration(v.document_release_due));
export const formatIfOverdue = (v) => (v === 'proceed_at_risk' ? 'Proceed at risk' : 'Wait for endorsement');

export const POSITION_LABEL = {
  execution: 'Executing Party',
  verification: 'Verification Party',
  acceptance: 'Acceptance Party',
};

export const VISIBILITY_ACCEPTANCE = ['acceptance_sees_ev_ncrs', 'acceptance_sees_ev_rfis'];
export const VISIBILITY_EXECUTING = ['executing_sees_va_ncrs', 'executing_sees_va_rfis'];
export const formatAi = (v) => (v && v.allowed ? 'Allowed' : 'Not allowed');

const setKey = (refs) => (refs || []).map(canonical).sort().join('|');
/** Order does not change who prepares: compared as a set. */
export const samePreparedBy = (a, b) => setKey(a) === setKey(b);

// ---------------------------------------------------------------------------
// QMP-FE-7 — THE CHOICES ARE THE SERVER'S (`GET /qmp` `standard_options`, D22)
// ---------------------------------------------------------------------------

/** The served choices of one field: `options[section][field].options`, each
 *  `{label, value, source?, input?}`. `[]` until the read has answered. */
export const fieldOptions = (options, section, field) => (
  (options && options[section] && options[section][field]
    && options[section][field].options) || []);

/** `{key, label, value}[]` for a select from the SERVED choices, the current
 *  value appended when it is not one of them (mockup `gsel`: a value outside
 *  the list stays visible) — labelled by `fallback`. */
export function selectOptions(served, current, fallback, same = sameValue) {
  const opts = (served || []).map((o) => ({ key: canonical(o.value), label: o.label, value: o.value }));
  const hit = opts.find((o) => same(o.value, current));
  if (hit) return { options: opts, selected: hit.key };
  const extra = { key: canonical(current), label: fallback ? fallback(current) : '', value: current };
  return { options: opts.concat([extra]), selected: extra.key };
}
